/**
 * Heading / prompt helpers for guided lessons: dedupe step title vs activity title,
 * shorten tap-round questions, and suppress generic engine labels.
 */

/** @param {unknown} s */
export function normalizeHeading(s) {
  if (typeof s !== 'string') return '';
  return s.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Normalized generic activity titles — safe to hide when step title carries the task. */
export const GENERIC_ACTIVITY_TITLE_BLOCKLIST = new Set(
  [
    'Who lives where?',
    'Habitat challenge',
    'Clue to home',
    'Quick check',
    'Pattern challenge',
    'Sequence challenge',
    'Match each clue',
    'Quiz',
    'Explore',
    'Place each item',
    'Match together',
    'Habitat gallery',
    'Try it',
    'Order the loop program',
    'Order moves',
    'Order the program',
    'Pick the slow-down gear',
    'Pick the speed-up gear',
    'Pick the same-spin gears',
    'Opposite spin',
    'Longer train',
    'Fastest gear',
    'Turn ratio',
    'Slowest gear',
    'Reorder the moves',
  ].map((t) => normalizeHeading(t)),
);

/** @param {unknown} stepTitle @param {unknown} activityTitle */
export function shouldSuppressActivityTitle({ stepTitle, activityTitle }) {
  const st = normalizeHeading(stepTitle);
  const at = normalizeHeading(activityTitle);
  if (!at) return true;
  if (!st) return GENERIC_ACTIVITY_TITLE_BLOCKLIST.has(at);
  if (st === at) return true;
  if (st.includes(at) || at.includes(st)) return true;
  if (GENERIC_ACTIVITY_TITLE_BLOCKLIST.has(at)) {
    return (
      st.length >= 10 ||
      /[?]/.test(String(stepTitle)) ||
      /\b(try|pick|match|where|which|tap|place|drag|swipe|order|sort)\b/i.test(String(stepTitle))
    );
  }
  return false;
}

/**
 * @param {'placement'|'clue'|'sequence'|'fit'} kind
 * @returns {string}
 */
export function shortPlacementQuestion(kind) {
  switch (kind) {
    case 'placement':
      return 'Where does it live?';
    case 'clue':
      return 'What matches this clue?';
    case 'sequence':
      return 'What comes next?';
    case 'fit':
      return 'Which habitat fits?';
    default:
      return 'Where does it live?';
  }
}

/**
 * If question repeats the item label ("Where does Tree frog belong?"), prefer a short generic.
 * @param {string | undefined | null} question
 * @param {string | undefined | null} itemLabel
 * @param {boolean} [singleSurface]
 */
export function placementQuestionForTapRound(question, itemLabel, singleSurface = false) {
  const label = typeof itemLabel === 'string' ? itemLabel.trim() : '';
  const q = typeof question === 'string' ? question.trim() : '';
  if (!singleSurface || !label) return q || shortPlacementQuestion('placement');
  const nl = normalizeHeading(label);
  const nq = normalizeHeading(q);
  const escaped = nl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const whereBelong = new RegExp(`^where does\\s+${escaped}\\s+belong\\??$`, 'i');
  const whereGo = new RegExp(`^where does\\s+${escaped}\\s+go\\??$`, 'i');
  if (whereBelong.test(q) || whereGo.test(q) || (nq.includes(nl) && /\bwhere\b/.test(nq) && /\bbelong|go|fit|live\b/.test(nq))) {
    return shortPlacementQuestion('placement');
  }
  return q || shortPlacementQuestion('placement');
}

/**
 * Word-set Jaccard similarity in [0,1].
 * @param {unknown} a
 * @param {unknown} b
 */
export function headingSimilarity(a, b) {
  const sa = normalizeHeading(a);
  const sb = normalizeHeading(b);
  if (!sa || !sb) return 0;
  const wa = new Set(sa.split(/\s+/).filter((w) => w.length > 2));
  const wb = new Set(sb.split(/\s+/).filter((w) => w.length > 2));
  if (wa.size === 0 || wb.size === 0) return 0;
  let inter = 0;
  for (const w of wa) if (wb.has(w)) inter += 1;
  return inter / (wa.size + wb.size - inter);
}
